import {
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  getSortedRowModel,
  getCoreRowModel,
  PaginationState,
  useReactTable,
  SortingState,
  toast,
} from '@ui/components';
import { RequestFieldFilterType, ResponseErrorDto, NotificationDto } from '@interfaces/models';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNotificationsListStore } from '@pages/notifications/stores';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetNotifications } from '@shared/hooks';
import { markAsRead } from '@api/notifications';
import { getTableRequest } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useDebounce } from '@ui/hooks';
import { z } from 'zod';

import { getNotificationsColumns } from './notifications-table-columns';

export const NOTIFICATIONS_TABLE_FILTERS_DEFAULT_VALUES = {
  [RequestFieldFilterType.NOTIFICATION_READ_STATUS]: [],
  [RequestFieldFilterType.NOTIFICATION_TYPE]: [],
};

const filterSchema = z.object({
  [RequestFieldFilterType.NOTIFICATION_READ_STATUS]: z.array(z.string()),
  [RequestFieldFilterType.NOTIFICATION_TYPE]: z.array(z.string()),
});

export const useNotificationsTable = () => {
  // Use a ref to store the last known totalItems value.
  // This is used to avoid flickering when the data is loading.
  // I do not want to use placeholderData in useQuery because loader is not visible then when changing pagination.
  const { setSelectedNotification, setIsRemoveNotificationDialogOpen } =
    useNotificationsListStore();
  const { t } = useTranslation();
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const debouncedTableSearchQuery = useDebounce(tableSearchQuery, 800);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const totalItemsRef = useRef<number>(0);
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const filtersFrom = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: NOTIFICATIONS_TABLE_FILTERS_DEFAULT_VALUES,
  });

  const tableFilters = filtersFrom.watch();

  const request = useMemo(
    () =>
      getTableRequest({
        search: debouncedTableSearchQuery,
        filters: tableFilters,
        pagination,
        sorting,
      }),
    [pagination, sorting, debouncedTableSearchQuery, tableFilters],
  );

  const { data: apiData, isLoading, refetch } = useGetNotifications({ request });

  const { mutate: onMarkAsRead } = useMutation({
    mutationFn: (notification: NotificationDto) => markAsRead(notification.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
      toast.success(t('NOTIFICATIONS.MARK_AS_READ_SUCCESS'));
      refetch();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const onRemoveNotification = useCallback(
    (notification: NotificationDto) => {
      setSelectedNotification(notification);
      setIsRemoveNotificationDialogOpen(true);
    },
    [setSelectedNotification, setIsRemoveNotificationDialogOpen],
  );

  const columns = useMemo(
    () => getNotificationsColumns(onRemoveNotification, onMarkAsRead),
    [onRemoveNotification, onMarkAsRead],
  );

  // Update the ref whenever we get valid data
  useEffect(() => {
    if (apiData?.pagination?.totalItems !== undefined) {
      totalItemsRef.current = apiData.pagination.totalItems;
    }
  }, [apiData?.pagination?.totalItems]);

  // Get the current totalItems - prioritize actual data if available. Needed on first render.
  const totalItems =
    apiData?.pagination?.totalItems !== undefined
      ? apiData.pagination.totalItems
      : totalItemsRef.current;

  useDeepCompareEffect(() => {
    setPagination((current) => ({
      ...current,
      pageIndex: 0,
    }));
  }, [debouncedTableSearchQuery, tableFilters]);

  const table = useReactTable({
    getRowId: (row) => row.id as unknown as string,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    state: { pagination, sorting, globalFilter: tableSearchQuery, columnFilters, rowSelection },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    onGlobalFilterChange: setTableSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    data: apiData?.data ?? [],
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalItems,
    manualSorting: true,
    columns: columns,
  });

  return {
    columnsLength: columns.length,
    state: table.getState(),
    refetchTable: refetch,
    data: apiData,
    filtersFrom,
    totalItems,
    isLoading,
    table,
  };
};
